import FormattedMessage from 'I18n/components/FormattedMessage';
import getLang from 'I18n/utils/getLang';
import { Button } from 'foundations-components/transitional/components/Button';
import { FloatingAlertList, FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { getDataFetchingClientRequestState } from 'marketplace-ui-common/dataFetchingClient/utils/getDataFetchingClientRequestState';
import LoadingWrapper from 'marketplace-ui-common/layout/components/LoadingWrapper';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useRouteMatch } from 'react-router-dom';
import { useCreateLeadContactMutation } from '../__generated__/chirp/com/hubspot/marketplace/analytics/rpc/LeadGenerationRpc.hooks';
import { useIsStandardizedInstallQuery } from '../__generated__/chirp/com/hubspot/marketplace/listing/details/rpc/MarketplaceListingDetailsRpc-app.hooks';
import { PlanCombinationsModal } from './compatibility/PlanCombinationsModal';
import { DetailsPageContext } from './context/DetailsPageContext';
import { DetailsError } from './error/DetailsError';
import { useListingPreview } from './hooks/useListingPreview';
import { useTracking } from './hooks/useTracking';
import { useUnifiedDetails } from './hooks/useUnifiedDetails';
import { DetailsPage } from './layout/DetailsPage';
import { ProductBuyBox } from './layout/cards/ProductBuyBox';
import { ProductFeaturesCard } from './layout/cards/ProductFeaturesCard';
import { ProductFeedbackCard } from './layout/cards/ProductFeedbackCard';
import { ProductFunctionalityCard } from './layout/cards/ProductFunctionalityCard';
import { ProductInfoCard } from './layout/cards/ProductInfoCard';
import { ProductOverviewCard } from './layout/cards/ProductOverviewCard';
import { ProductPricingCard } from './layout/cards/ProductPricingCard';
import { ProductRequirementsCard } from './layout/cards/ProductRequirementsCard';
import { ProductReviewsCard } from './layout/cards/ProductReviewsCard';
import { HSPageViewTracker } from './utils/HSPageViewTracker';

// @ts-expect-error ts-migrate(2307) FIXME: Cannot find module 'bender-url!ecosystem-marketpla... Remove this comment to see the full error message
import fallbackAgentOverviewImage from 'bender-url!marketplace-ui-details/img/agent-overview-fallback.png';
import { useGetRequiredScopeGroupsQuery } from '../__generated__/chirp/com/hubspot/marketplace/installs/rpc/MarketplaceInstallsRpc.hooks';
import { useGetStandardizedInstallOptInQuery } from '../__generated__/chirp/com/hubspot/marketplace/listing/details/rpc/StandardizedInstallOptInRpc.hooks';
import { McpServerConnectAlert } from './functionality/McpServerConnectAlert';
import { AppGovernanceAlert } from './governance/AppGovernanceAlert';
import { useInstallability } from './hooks/useInstallability';
import { MarketplaceListingCTAButton } from './install/MarketplaceListingCTAButton';
import { ProductInfoList } from './layout/cards/ProductInfoList';
import { ProductPartnerTierCard } from './layout/cards/ProductPartnerTierCard';
import { getAppDetailsShortlink } from './utils/DetailsPageUrls';
import { getAppInstallType } from './utils/getAppInstallType';
import { buildSolutionsPartnerProfileInfoListSections } from './utils/productInfoListUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function Details({
  appContext,
  logInUrl,
  // To be used in the Public Marketplace when the CTA reads "Sign in"
  previewListingId,
  // Will be used to fetch listing preview data
  language,
  renderedErrorLink,
  slug,
  userInfo,
  isEmbedded = false,
  onEmbedClose,
  onInstallComplete,
  onInstallError,
  isUngatedForPartnerTierCard = false
}) {
  const {
    path
  } = useRouteMatch();
  const isReviewLink = path.includes('listing/:listingSlug/write-review');
  const isSolutionsPartnerProfile = slug === null || slug === void 0 ? void 0 : slug.includes('profile:');
  useEffect(() => {
    if (isReviewLink && slug && appContext !== 'app') {
      window.location.href = `${getAppDetailsShortlink(slug)}/write-review`;
    }
  }, [isReviewLink, appContext, slug]);
  const published = !!slug && !previewListingId;
  const isPreview = !!previewListingId;
  const [compatibilityModalOpen, setCompatibilityModalOpen] = useState(false);
  const {
    pathname
  } = useLocation();

  // This will be the only call to Unified Details Service
  // Once everything is migrated to V3
  const {
    unifiedDetailsData: unifiedDetailsDataV3,
    unifiedDetailsLoading: unifiedDetailsLoadingV3,
    unifiedDetailsError: unifiedDetailsErrorV3
  } = useUnifiedDetails({
    appContext,
    slug: slug || '',
    language: getLang(),
    skip: !slug || isPreview,
    version: 3
  });

  // Additional call to Unified Details Service V2
  // To get ids from the legacy system
  const {
    unifiedDetailsData: unifiedDetailsDataV2
  } = useUnifiedDetails({
    appContext,
    slug: slug || '',
    language: getLang(),
    skip: !slug || isPreview || isSolutionsPartnerProfile,
    version: 2
  });
  const {
    previewData,
    previewLoading,
    previewError
  } = useListingPreview({
    appContext,
    language: language || getLang(),
    listingId: previewListingId,
    skip: !!slug || !isPreview
  });
  const unifiedDetailsRequestStatusV3 = getDataFetchingClientRequestState(unifiedDetailsErrorV3, unifiedDetailsLoadingV3);
  const previewRequestStatus = getDataFetchingClientRequestState(previewError, previewLoading);

  // UDS V3 data from Framework
  const {
    agencyPricingUrl,
    agentType,
    betaDetails,
    budgetChoice: budget,
    builtByHubSpot,
    connectionType,
    caseStudyUrl,
    category,
    certifiedAt,
    companyName,
    companyUrl,
    contactForm,
    credentials,
    currency,
    description,
    displayTag,
    facebookUrl,
    features,
    installCount,
    isUserLevel,
    languages,
    listingIcon,
    listingId,
    liveChatUrl,
    meetingsUrl,
    name,
    overview,
    industryChoice: industries,
    isDashworks,
    isMcpConnector,
    isMcpServer,
    isOpsHub,
    mcpFaqs,
    mcpTools,
    mcpUseCases,
    offeringId,
    partnerPortalId,
    partnerTier,
    partnerTierLabel,
    partnerType,
    phone,
    physicalOfficeLocations,
    pricingPlans,
    pricingPlansUrl,
    privacyUrl1,
    privacyUrl2,
    privacyUrl3,
    productFeatures: frameworkProductFeatures,
    productType,
    profileId,
    regionChoice: regions,
    remoteLocations,
    screenshots: frameworkScreenshots,
    services,
    setupDocsUrl,
    sharedDataInfo,
    sharedDataHow,
    sharedObjects,
    sourceId,
    supportEmail,
    supportLanguages,
    supportUrl,
    tagline,
    termsUrl,
    tier,
    ubbInfo,
    urlSlug,
    lastPublishedAt,
    worksWithBreeze,
    videos: frameworkVideos
  } = previewData || unifiedDetailsDataV3;
  const mcpServerData = useMemo(() => {
    if (!isMcpServer || !mcpUseCases) {
      return undefined;
    }
    return {
      mcpFaqs,
      mcpTools,
      mcpUseCases
    };
  }, [isMcpServer, mcpFaqs, mcpTools, mcpUseCases]);

  // Remaining App Details data from Unified Details Service V2
  const {
    id
  } = previewData || unifiedDetailsDataV2;
  const reviewableId = offeringId || profileId;
  let iconsListSections;
  switch (productType) {
    case 'SOLUTIONS_PARTNER_PROFILE':
      {
        iconsListSections = buildSolutionsPartnerProfileInfoListSections({
          budget,
          credentials,
          industries,
          languages,
          regions,
          services
        });
        break;
      }
    default:
      iconsListSections = undefined;
  }
  const [createLeadContact] = useCreateLeadContactMutation();

  // Standardized install gate check via RPC (for STANDARD connectionType)
  const {
    data: standardizedInstallData
  } = useIsStandardizedInstallQuery({
    variables: {
      listingId: listingId || 0
    },
    skip: !listingId || appContext !== 'app' || productType !== 'APP' || connectionType !== 'STANDARD'
  });
  const isStandardizedInstall = standardizedInstallData === null || standardizedInstallData === void 0 ? void 0 : standardizedInstallData.standardizedInstall;
  const screenshots = useMemo(() => {
    // Filter out screenshot objects with empty values
    const screenshotsWithValues = frameworkScreenshots === null || frameworkScreenshots === void 0 ? void 0 : frameworkScreenshots.filter(s => s.screenshot.value);
    return screenshotsWithValues !== null && screenshotsWithValues !== void 0 && screenshotsWithValues.length ? screenshotsWithValues :
    // Default to agent fallback image
    productType === 'AGENT' ? [{
      screenshot: {
        value: fallbackAgentOverviewImage,
        altText: ''
      }
    }] : [];
  }, [frameworkScreenshots, productType]);
  const videos = useMemo(() => {
    // Filter out video objects with empty values
    return frameworkVideos === null || frameworkVideos === void 0 ? void 0 : frameworkVideos.filter(s => s.video.value);
  }, [frameworkVideos]);
  const filteredFeatures = useMemo(() => {
    // Filter out feature objects with empty values
    return features === null || features === void 0 ? void 0 : features.filter(feature => feature.featureName);
  }, [features]);
  const tools = useMemo(() => {
    return frameworkProductFeatures === null || frameworkProductFeatures === void 0 ? void 0 : frameworkProductFeatures.filter(feature => feature.data.isTool);
  }, [frameworkProductFeatures]);
  const appObjects = useMemo(() => {
    return frameworkProductFeatures === null || frameworkProductFeatures === void 0 ? void 0 : frameworkProductFeatures.filter(feature => feature.type === 'APP_OBJECT');
  }, [frameworkProductFeatures]);
  const appCards = useMemo(() => {
    return frameworkProductFeatures === null || frameworkProductFeatures === void 0 ? void 0 : frameworkProductFeatures.filter(feature => feature.type === 'UIE' &&
    // if any of these fields are missing, don't render the card
    feature.name && feature.data.description && feature.data.previewImage && feature.data.eligibleLocations);
  }, [frameworkProductFeatures]);

  // TODO: remove opt in logic once standardized install is turned on for all listings permanently
  const {
    data: optInData
  } = useGetStandardizedInstallOptInQuery({
    variables: {
      listingId: previewListingId || 0
    },
    skip: !previewListingId || appContext === 'admin-preview' //skip if not in listing preview
  });
  const isPreviewAndOptedInToStandardInstall = (optInData === null || optInData === void 0 ? void 0 : optInData.optedIn) || false;
  const appInstallType = getAppInstallType({
    connectionType,
    isDashworks,
    isOpsHub,
    isStandardizedInstall,
    isPreviewAndOptedInToStandardInstall
  });
  const {
    data: installabilityData,
    loading: installabilityLoading
  } = useInstallability({
    appContext,
    listingId,
    published,
    skip: !listingId || isPreview
  });
  const {
    data: requiredScopeGroupsData
  } = useGetRequiredScopeGroupsQuery({
    variables: {
      listingId: listingId
    },
    skip: !listingId || isPreview
  });
  const neededScopeGroups = useMemo(() => {
    return requiredScopeGroupsData === null || requiredScopeGroupsData === void 0 ? void 0 : requiredScopeGroupsData.scopeGroups;
  }, [requiredScopeGroupsData]);
  const installed = useMemo(() => (installabilityData === null || installabilityData === void 0 ? void 0 : installabilityData.installed) || false, [installabilityData]);
  const {
    trackInstall,
    trackInteraction,
    trackPageViewed
  } = useTracking({
    appContext,
    companyName,
    embed: isEmbedded,
    installed,
    isPreview,
    isReviewLink,
    listingId,
    slug: isEmbedded ? slug : urlSlug,
    sourceId,
    type: productType
  });
  const pageViewTracked = useRef(false);
  useEffect(() => {
    pageViewTracked.current = false;
    const floatingAlerts = FloatingAlertStore.getAlerts();
    Object.keys(floatingAlerts).forEach(alertId => {
      FloatingAlertStore.removeAlert(alertId);
    });
  }, [slug]);
  useEffect(() => {
    if (!installabilityLoading && !pageViewTracked.current && !unifiedDetailsLoadingV3) {
      trackPageViewed();
      pageViewTracked.current = true;
    }
  }, [installabilityLoading, installed, isEmbedded, pageViewTracked, trackPageViewed, unifiedDetailsLoadingV3]);
  const runLeadGen = useCallback(({
    action
  }) => {
    if (productType !== 'APP' || appContext !== 'app' || !published) {
      return;
    }
    trackInteraction({
      action: 'generated lead',
      value: `${partnerPortalId}`
    });
    if (action && listingId) {
      createLeadContact({
        variables: {
          listingId,
          action
        }
      }).catch(error => {
        console.error('Error sending lead:', error);
      });
    }
  }, [appContext, createLeadContact, listingId, partnerPortalId, productType, published, trackInteraction]);
  const renderedPrimaryCTA = useMemo(() => {
    // Public "Sign in" buttons that redirect to portal picker
    if (appContext === 'public') {
      return /*#__PURE__*/_jsx(Button, {
        use: "primary",
        href: logInUrl,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `detailsPage.productDetailsCard.cta.signIn.${productType}`
        })
      });
    }
    return /*#__PURE__*/_jsx(MarketplaceListingCTAButton, {
      appContext: appContext,
      buttonProps: {
        onEmbedClose,
        onInstallComplete,
        onInstallError
      },
      isEmbedded: isEmbedded,
      inDetailsPageContext: true,
      previewListingId: previewListingId,
      slug: isSolutionsPartnerProfile ? `profile:${urlSlug}` : urlSlug,
      userInfo: userInfo
    });
  }, [appContext, isEmbedded, isSolutionsPartnerProfile, logInUrl, onEmbedClose, onInstallComplete, onInstallError, previewListingId, productType, urlSlug, userInfo]);
  if (unifiedDetailsErrorV3) {
    return /*#__PURE__*/_jsx(DetailsError, {
      renderedErrorLink: renderedErrorLink,
      unifiedDetailsError: unifiedDetailsErrorV3
    });
  }
  return /*#__PURE__*/_jsx(LoadingWrapper, {
    requestStatus: [unifiedDetailsRequestStatusV3, previewRequestStatus],
    children: /*#__PURE__*/_jsxs(DetailsPageContext.Provider, {
      value: {
        appContext,
        displayTag,
        iconUrl: listingIcon === null || listingIcon === void 0 ? void 0 : listingIcon.value,
        listingId,
        listingIdV2: id,
        // TODO - deprecate --- this is still needed for sendAdminEmail
        previewListingId,
        name,
        productType,
        published,
        runLeadGen,
        trackInteraction,
        trackInstall,
        userInfo,
        isEmbedded,
        isMcpServer,
        isMcpConnector,
        worksWithBreeze
      },
      children: [/*#__PURE__*/_jsx(FloatingAlertList, {}), /*#__PURE__*/_jsx(HSPageViewTracker, {
        pathname: pathname,
        overrideTrack: appContext === 'app',
        addLang: appContext === 'app'
      }), productType === 'APP' && appContext === 'app' && /*#__PURE__*/_jsx(AppGovernanceAlert, {
        appId: parseInt(sourceId, 10),
        appName: name
      }), /*#__PURE__*/_jsx(DetailsPage, {
        leftColumn: /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(ProductBuyBox, {
            agentType: agentType,
            betaDetails: betaDetails,
            certifiedAt: certifiedAt,
            companyName: companyName || 'HubSpot',
            companyUrl: companyUrl,
            contactForm: contactForm,
            installCount: installCount,
            meetingsUrl: meetingsUrl,
            tagline: tagline,
            partnerType: partnerType,
            physicalOfficeLocations: physicalOfficeLocations,
            primaryCTA: renderedPrimaryCTA,
            productId: offeringId || profileId || 0,
            remoteLocations: remoteLocations,
            sourceId: sourceId,
            tier: tier
          }), isUngatedForPartnerTierCard && /*#__PURE__*/_jsx(ProductPartnerTierCard, {
            partnerTier: partnerTier,
            partnerTierLabel: partnerTierLabel
          }), !isSolutionsPartnerProfile && /*#__PURE__*/_jsx(ProductRequirementsCard, {
            installType: appInstallType,
            features: filteredFeatures || [],
            builtByHubSpot: builtByHubSpot,
            isUserLevel: isUserLevel,
            neededScopeGroups: neededScopeGroups,
            onCompatibilityModalOpen: () => setCompatibilityModalOpen(true),
            pricingPlans: pricingPlans || []
          }), iconsListSections && /*#__PURE__*/_jsx(ProductInfoList, {
            sections: iconsListSections
          })]
        }),
        rightColumn: /*#__PURE__*/_jsxs(_Fragment, {
          children: [isMcpServer && appContext === 'app' && /*#__PURE__*/_jsx(McpServerConnectAlert, {}), /*#__PURE__*/_jsx(ProductOverviewCard, {
            caseStudyUrl: caseStudyUrl
            // TODO: Remove `description` once SOLUTIONS_PARTNER_PROFILE listings use `overview` instead
            ,
            overview: overview || description,
            screenshots: screenshots,
            setupDocsUrl: setupDocsUrl,
            videos: videos
          }), /*#__PURE__*/_jsx(ProductFeaturesCard, {
            features: filteredFeatures || [],
            onCompatibilityModalOpen: () => setCompatibilityModalOpen(true)
          }), /*#__PURE__*/_jsx(ProductFunctionalityCard, {
            mcpServer: mcpServerData,
            sharedDataHow: sharedDataHow,
            sharedDataInfo: sharedDataInfo,
            sharedObjects: sharedObjects,
            appObjects: appObjects,
            tools: tools,
            appCards: appCards
          }), productType !== 'AGENT' && reviewableId && /*#__PURE__*/_jsx(ProductReviewsCard, {
            companyName: companyName,
            isPreview: isPreview,
            partnerPortalId: partnerPortalId,
            productId: reviewableId,
            supportUrl: supportUrl,
            userInfo: userInfo,
            isReviewLink: isReviewLink
          }), /*#__PURE__*/_jsx(ProductPricingCard, {
            agencyPricingUrl: agencyPricingUrl,
            currency: currency,
            pricingPlansUrl: pricingPlansUrl,
            pricingPlans: pricingPlans || [],
            ubbInfo: ubbInfo,
            pricingUpdatedAt: lastPublishedAt //TODO: replace with pricing-specific updatedAt when available from UDSV3
          }), !isSolutionsPartnerProfile && /*#__PURE__*/_jsx(ProductInfoCard, {
            details: {
              companyName: companyName || 'HubSpot',
              companyUrl,
              category,
              languages,
              installCount
            },
            support: {
              liveChatUrl,
              supportEmail,
              facebookUrl,
              builtByHubSpot,
              phone,
              supportLanguages,
              supportUrl
            },
            legal: {
              builtByHubSpot,
              privacyUrl1,
              privacyUrl2,
              privacyUrl3,
              termsUrl
            }
          }), productType === 'AGENT' && /*#__PURE__*/_jsx(ProductFeedbackCard, {
            partnerPortalId: partnerPortalId,
            logInUrl: logInUrl
          })]
        })
      }), compatibilityModalOpen && /*#__PURE__*/_jsx(PlanCombinationsModal, {
        features: filteredFeatures || [],
        onClose: () => setCompatibilityModalOpen(false)
      })]
    })
  });
}